import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from "@floating-ui/react";
import cn from "classnames";
import {
  MondayImportItemsSkeleton,
  MondayImportSkeleton,
} from "frontend/canvas-designer-new/monday-import-items/monday-import-skeleton";
import { useMondayImportItems } from "frontend/canvas-designer-new/monday-import-items/monday-import.hook";
import { PortalID } from "frontend/modal/portal-types";
import { TextInput } from "frontend/ui-components/design-system/text-input";
import { MagnifyingGlassIcon } from "frontend/ui-components/picker/magnifying-glass-icon";
import { RefObject, useCallback, useEffect, useState, useRef } from "react";
import { useOnClickOutside } from "usehooks-ts";
import styles from "./monday-import-items.module.css";
import { MondayItemsList } from "frontend/canvas-designer-new/monday-import-items/monday-items-list/monday-items-list";
import { MondayBreadcrumbs } from "frontend/canvas-designer-new/monday-import-items/monday-breadcrumbs/monday-breadcrumbs";
import { useMondayImportItemsContext } from "frontend/canvas-designer-new/monday-import-items/monday-import-items-context";
import { useMondayImportIntegration } from "frontend/canvas-designer-new/monday-import-items/monday-import-integration.hook";
import type { PointInViewport } from "frontend/utils/math-utils";
import { CloseIcon } from "frontend/icons/close";
import { groupBy, prop } from "rambda";
import { MondayBoardSelector } from "frontend/canvas-designer-new/monday-import-items/monday-board-selector/monday-board-selector";
import { ResourceType } from "./monday-import-items.types";

export default function MondayImportItems({
  relativeTo,
  onDismiss,
}: {
  relativeTo: RefObject<HTMLElement>;
  onDismiss: () => void;
}) {
  // Set up floating UI
  const portal = document.getElementById(PortalID.Floaters);
  const [isAddingItems, setIsAddingItems] = useState(false);
  const { refs, floatingStyles } = useFloating({
    open: !!relativeTo.current,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 })],
    whileElementsMounted: autoUpdate,
  });
  useOnClickOutside(refs.floating, onDismiss, "mouseup");

  // Set the reference element when it changes
  useEffect(() => {
    if (relativeTo.current) {
      refs.setReference(relativeTo.current);
    }
  }, [relativeTo, refs]);

  const { addIntegration, addItems } = useMondayImportIntegration();

  const {
    //
    workspaces,
    workspacesLoading,
    workspacesInitialLoading,
    loadMoreWorkspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    //
    boards,
    boardsLoading,
    boardsInitialLoading,
    selectedBoard,
    setSelectedBoard,
    loadMoreBoards,
    setBoardSearch,
    //
    search,
    setSearch,
    //
    selectedItems,
    setSelectedItems,

    items,
    itemIdToBoardId,
    itemsInitialLoading,
    itemsLoading,
    hasMoreItems,
    loadMoreItems,
  } = useMondayImportItems();

  const { breadcrumbs, currentItem, clearBreadcrumbs } = useMondayImportItemsContext();
  const [currentItemColor, setCurrentItemColor] = useState<string | undefined>();

  // Reset currentItemColor when navigating back or when currentItem becomes null
  useEffect(() => {
    if (!currentItem) {
      setCurrentItemColor(undefined);
    }
  }, [currentItem]);

  // Track scroll positions for different views
  const scrollPositionsRef = useRef<Map<string, number>>(new Map());
  const [currentScrollPosition, setCurrentScrollPosition] = useState(0);

  // Keep a ref to selectedItems to avoid stale closures
  const selectedItemsRef = useRef<string[]>(selectedItems);
  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  }, [selectedItems]);

  // Create a unique key for the current view
  const viewKey = currentItem ? `sub-${currentItem.id}` : "main";

  // Save scroll position when view changes
  const previousViewKeyRef = useRef(viewKey);

  useEffect(() => {
    if (previousViewKeyRef.current !== viewKey) {
      // Save the previous view's scroll position
      scrollPositionsRef.current.set(previousViewKeyRef.current, currentScrollPosition);

      // Restore the new view's scroll position
      const savedPosition = scrollPositionsRef.current.get(viewKey) || 0;
      setCurrentScrollPosition(savedPosition);

      previousViewKeyRef.current = viewKey;
    }
  }, [viewKey, currentScrollPosition]);

  // Handle scroll updates from the list
  const handleScroll = useCallback((scrollTop: number) => {
    setCurrentScrollPosition(scrollTop);
  }, []);

  const addIntegrationItem = useCallback(
    async (itemsIds: string[], pos?: PointInViewport) => {
      if (!selectedBoard) {
        return;
      }

      const currentSelectedItems = selectedItemsRef.current;

      // When dragging, check if we should use selected items instead
      let finalItemIds = itemsIds;
      if (pos && currentSelectedItems.length > 0 && currentSelectedItems.includes(itemsIds[0])) {
        finalItemIds = currentSelectedItems;
      }

      setIsAddingItems(true);
      const itemsDetails = finalItemIds.map((itemId) => {
        return {
          itemId,
          ...itemIdToBoardId[itemId],
        };
      });

      const itemsByBoardId = groupBy(prop("boardId"), itemsDetails);

      // Collect all the board add-integration/add-items pairs as promises, then await all in parallel for better performance.
      const boardPromises = Object.entries(itemsByBoardId).map(async ([boardId, items]) => {
        const firstItemDetails = items.at(0);
        if (!firstItemDetails) {
          return;
        }
        try {
          const integrationId = await addIntegration({
            boardId: boardId,
            isSubitemsBoard: firstItemDetails.isSubItem,
            parentBoardId: selectedBoard?.id,
          });

          if (!integrationId) {
            return;
          }

          // Add diagonal offset for multiple items
          const DIAGONAL_OFFSET = 30; // pixels offset for each item
          const itemIds = items.map((item) => item.itemId);

          if (pos && itemIds.length > 1) {
            // Create positions for each item with diagonal offset
            const addItemsPromises = itemIds.map((itemId, index) => {
              const offsetPos: PointInViewport = {
                x: (pos as PointInViewport).x + index * DIAGONAL_OFFSET,
                y: (pos as PointInViewport).y + index * DIAGONAL_OFFSET,
              } as PointInViewport;
              return addItems({
                integrationId,
                items: [itemId],
                pos: offsetPos,
              });
            });
            await Promise.allSettled(addItemsPromises);
          } else {
            // Single item or no position specified
            await addItems({ integrationId, items: itemIds, pos });
          }
        } catch (error) {
          console.error("failed to add integration", error);
          throw error; // propagate error to abort all
        }
      });

      try {
        await Promise.all(boardPromises);
        setIsAddingItems(false);
        setSelectedItems([]);
      } catch (error) {
        console.error("failed to add integrations", error);
        setIsAddingItems(false);
        setSelectedItems([]);
      } finally {
        clearBreadcrumbs();
        setCurrentItemColor(undefined);
      }
    },
    [selectedBoard, itemIdToBoardId, addIntegration, addItems, clearBreadcrumbs, setSelectedItems]
  );

  const handleBoardSelect = useCallback(
    (pickedBoard: ResourceType) => {
      const fullBoard = boards.find((b) => b.id === pickedBoard.id);
      setSelectedBoard(fullBoard ?? null);
    },
    [boards, setSelectedBoard]
  );

  if (!relativeTo.current || !portal) return null;

  if (workspacesInitialLoading) {
    return (
      <FloatingPortal id={PortalID.Floaters}>
        <div ref={refs.setFloating} style={floatingStyles}>
          <div className={cn(styles.container, styles.skeletonContainer)}>
            <MondayImportSkeleton />
          </div>
        </div>
      </FloatingPortal>
    );
  }

  return (
    <FloatingPortal id={PortalID.Floaters}>
      <div ref={refs.setFloating} style={floatingStyles}>
        <div className={styles.container} id="monday-import-items-container">
          <div className={styles.closeButton} onClick={onDismiss}>
            <CloseIcon />
          </div>

          {breadcrumbs.length === 0 && (
            <div className={styles.header}>
              <div className={styles.title}>Import items from board</div>

              <div className={styles.headerContent}>
                <MondayBoardSelector
                  workspaces={workspaces}
                  selectedWorkspace={selectedWorkspace}
                  setSelectedWorkspace={setSelectedWorkspace}
                  workspacesLoading={workspacesLoading}
                  workspacesInitialLoading={workspacesInitialLoading}
                  loadMoreWorkspaces={loadMoreWorkspaces}
                  boards={boards}
                  selectedBoard={selectedBoard}
                  setSelectedBoard={handleBoardSelect}
                  boardsLoading={boardsLoading}
                  boardsInitialLoading={boardsInitialLoading}
                  loadMoreBoards={loadMoreBoards}
                  pickerWidth="156px"
                  onSearch={setBoardSearch}
                />

                <div className={styles.searchContainer}>
                  <TextInput
                    leftIcon={<MagnifyingGlassIcon />}
                    variant="small"
                    placeholder="Search board's items"
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                    clearable={true}
                  />
                </div>
              </div>
            </div>
          )}
          {breadcrumbs.length > 0 && <MondayBreadcrumbs />}
          {(breadcrumbs.length === 0 || itemsInitialLoading) && <div className={styles.divider} />}

          {itemsInitialLoading ? (
            <div className={styles.skeletonContainer}>
              <MondayImportItemsSkeleton />
            </div>
          ) : (
            <MondayItemsList
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              searchValue={search}
              currentItem={currentItem}
              currentItemColor={currentItemColor}
              groups={items}
              loadMoreItems={loadMoreItems}
              hasMoreItems={hasMoreItems}
              itemsLoading={itemsLoading}
              onDragEnd={({ item, pos }) => addIntegrationItem([item.id], pos)}
              scrollPosition={currentScrollPosition}
              onScroll={handleScroll}
              onSubItemsClick={(_item, groupColor) => {
                setCurrentItemColor(groupColor);
              }}
            />
          )}

          <div className={styles.overlay} />
          {!itemsInitialLoading && (
            <button
              disabled={selectedItems.length === 0 || isAddingItems}
              className={styles.importButton}
              onClick={async () => {
                try {
                  await addIntegrationItem(selectedItems);
                } finally {
                  onDismiss();
                }
              }}
            >
              {isAddingItems
                ? "Importing..."
                : `Import ${selectedItems.length} ${selectedItems.length === 1 ? "item" : "items"}`}
            </button>
          )}
        </div>
      </div>
    </FloatingPortal>
  );
}
